import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { loc, locArr } from "@/lib/localize";
import { ShieldCheck, Layers, LineChart, Wallet, Building2 } from "lucide-react";

export function BrokerCard({ broker }) {
  const { isAr } = useLang();
  const name = loc(broker, "name", isAr);
  const initials = broker.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className="flex flex-col rounded-xl border border-hair bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
      data-testid={`broker-card-${broker.slug}`}
    >
      <div className="flex items-center gap-3">
        {broker.logo_url ? (
          <img src={broker.logo_url} alt={name} className="h-12 w-12 rounded-lg object-contain border border-hair bg-white p-1" />
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy font-heading text-sm font-bold text-gold">
            {initials}
          </span>
        )}
        <div>
          <h3 className="font-heading text-lg font-semibold text-ink leading-tight">{name}</h3>
          <span className="mt-0.5 inline-block rounded bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-subtle">
            {isAr ? "شريك مبدئي" : "Placeholder partner"}
          </span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <Meta icon={ShieldCheck} label={isAr ? "التنظيم" : "Regulation"} value={locArr(broker, "regulatory_jurisdictions", isAr).join(", ") || "—"} />
        <Meta icon={Wallet} label={isAr ? "أدنى إيداع" : "Min. Deposit"} value={broker.min_deposit || "—"} />
        <Meta icon={Layers} label={isAr ? "المنصات" : "Platforms"} value={locArr(broker, "platforms", isAr).slice(0, 2).join(", ")} />
        <Meta icon={LineChart} label={isAr ? "الأسواق" : "Markets"} value={locArr(broker, "markets", isAr).length + (isAr ? " سوق" : " markets")} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {locArr(broker, "account_types", isAr).slice(0, 3).map((a) => (
          <span key={a} className="rounded-md border border-hair bg-canvas px-2 py-0.5 text-xs text-subtle">{a}</span>
        ))}
      </div>

      <div className="mt-6 flex gap-3 pt-4 border-t border-hair">
        <Button asChild variant="outline" className="flex-1" data-testid={`broker-view-${broker.slug}`}>
          <Link to={`/broker-partners/${broker.slug}`}>{isAr ? "عرض الوسيط" : "View Broker"}</Link>
        </Button>
        <Button asChild className="flex-1 bg-finblue hover:bg-finblueDark" data-testid={`broker-open-${broker.slug}`}>
          <Link to={`/broker-partners/${broker.slug}`}>{isAr ? "فتح حساب" : "Open Account"}</Link>
        </Button>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-finblue" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-subtle/70">{label}</p>
        <p className="truncate font-medium text-ink">{value}</p>
      </div>
    </div>
  );
}
